import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Button, Input, Typography } from 'antd';
import { MessageOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type ChatMessage } from '@/apis/storefrontApi';
import styles from './index.module.less';

const POLL_INTERVAL_MS = 2500;
/** 本地仅存储会话 id 与用户临时 id，聊天数据均从接口按 sessionId 拉取 */
const STORAGE_SESSION_ID = 'chat_session_id';
const STORAGE_USER_ID = 'chat_user_id';
const STORAGE_LEGACY_SESSION_KEY = 'chat_session_key';
const STORAGE_LEGACY_VISITOR_ID = 'chat_visitor_id';

function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem(STORAGE_SESSION_ID);
    if (id && /^[a-f0-9-]{36}$/i.test(id)) return id;
    const legacy =
      localStorage.getItem(STORAGE_LEGACY_SESSION_KEY) ||
      localStorage.getItem(STORAGE_LEGACY_VISITOR_ID);
    if (legacy && /^[a-f0-9-]{36}$/i.test(legacy)) {
      localStorage.setItem(STORAGE_SESSION_ID, legacy);
      return legacy;
    }
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(STORAGE_SESSION_ID, id);
    return id;
  } catch {
    return `local-${Date.now()}`;
  }
}

function getOrCreateUserId(): string {
  try {
    let id = localStorage.getItem(STORAGE_USER_ID);
    if (id && id.length > 0) return id;
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(STORAGE_USER_ID, id);
    return id;
  } catch {
    return `u-${Date.now()}`;
  }
}

/** 消息时间展示：仅时间点或短日期+时间 */
function formatMessageTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    if (sameDay) {
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

/** 其他页面派发此事件可打开聊天面板，如 Contact 页的「实时客服」 */
export const CHAT_OPEN_EVENT = 'storefront-open-chat';

const ChatWidget: React.FC = () => {
  const { t } = useTranslation();
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [userId] = useState(() => getOrCreateUserId());
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastMessageIdRef = useRef<string | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const prevOpenRef = useRef(false);

  const fetchMessages = useCallback(() => {
    storefrontApi
      .getChatMessages(sessionId)
      .then(list => {
        setMessages(list);
        if (list.length > 0) {
          const last = list[list.length - 1];
          if (last.from === 'manager' && last.id !== lastMessageIdRef.current) {
            if (!open) setUnreadCount(c => c + 1);
            lastMessageIdRef.current = last.id;
          }
        }
      })
      .catch(() => {});
  }, [sessionId, open]);

  useEffect(() => {
    if (!open) return;
    fetchMessages();
    const timer = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [open, fetchMessages]);

  useEffect(() => {
    if (open && prevOpenRef.current === false) {
      setUnreadCount(0);
    }
    prevOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(CHAT_OPEN_EVENT, handler);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, handler);
  }, []);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    try {
      await storefrontApi.sendChatMessage(text, sessionId, userId);
      await fetchMessages();
    } catch {
      // 发送失败可考虑 toaster
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Badge count={unreadCount} size='small' offset={[-2, 2]}>
        <Button
          type='primary'
          shape='circle'
          size='large'
          className={styles.fab}
          icon={<MessageOutlined />}
          onClick={() => setOpen(true)}
        />
      </Badge>

      {open && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <Typography.Text strong>{t('chat.title')}</Typography.Text>
            <Button
              type='text'
              size='small'
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
            />
          </div>
          <div className={styles.panelBody}>
            <div className={styles.messageList}>
              {messages.map(m => (
                <div
                  key={m.id}
                  className={
                    m.from === 'client' ? styles.msgUser : styles.msgStaff
                  }
                >
                  <span className={styles.msgContent}>{m.content}</span>
                  <span className={styles.msgTime}>
                    {formatMessageTime(m.createdAt)}
                    {m.from === 'client' && m.readByManagerAt != null && m.readByManagerAt > 0 && (
                      <span className={styles.msgRead}> · 已读</span>
                    )}
                  </span>
                </div>
              ))}
              <div ref={listEndRef} />
            </div>
            <div className={styles.panelFooter}>
              <Input.TextArea
                value={input}
                onChange={e => setInput(e.target.value)}
                onPressEnter={e => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={t('chat.placeholder')}
                autoSize={{ minRows: 1, maxRows: 4 }}
                className={styles.input}
              />
              <Button
                type='primary'
                loading={sending}
                onClick={handleSend}
                className={styles.sendBtn}
              >
                {t('chat.send')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
